import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';

export type SlotListFormState = {
  formOffset: number;
  formDuration: number;
  formAvailableDuration: number;
  formSlotType: 'Session' | 'Break';
  formInsertBeforeSlotId?: string;
  formInsertAfterSlotId?: string;
  isUntimedFormActive: boolean;
  isFormActive: boolean;
};

export const END_GAP_ANCHOR_ID = '__end_gap__';

export type SlotListFormApi = SlotListFormState & {
  setFormAvailableDuration: (value: number) => void;
  setFormSlotType: (value: 'Session' | 'Break') => void;
  resetFormState: () => void;
  handleFormClose: () => void;
  handleFormSuccess: () => void;
  handleFormDurationChange: (duration: number) => void;
  handleShowForm: (
    gapSlotIdOrPosition: string,
    offset: number,
    slotType?: 'Session' | 'Break',
    insertBeforeSlotId?: string,
    insertAfterSlotId?: string,
  ) => void;
};

type Params = {
  flow?: FlowDto;
  previewFlow?: FlowDto;
  localSlots?: FlowSlotDto[];
  minuteBeforeMidnight: number;
  isEditingEndTiming: boolean;
  editingTiming: boolean;
};

export function useSlotListForm({
  flow,
  previewFlow,
  localSlots,
  minuteBeforeMidnight,
  isEditingEndTiming,
  editingTiming,
}: Params): SlotListFormApi {
  const [formOffset, setFormOffset] = useState<number>(0);
  const [formDuration, setFormDuration] = useState<number>(30);
  const [formAvailableDuration, setFormAvailableDuration] = useState<number>(30);
  const [formSlotType, setFormSlotType] = useState<'Session' | 'Break'>('Session');
  const [formInsertBeforeSlotId, setFormInsertBeforeSlotId] = useState<string | undefined>(undefined);
  const [formInsertAfterSlotId, setFormInsertAfterSlotId] = useState<string | undefined>(undefined);
  const [isUntimedFormActive, setIsUntimedFormActive] = useState<boolean>(false);

  const resetFormState = useCallback(() => {
    setFormOffset(0);
    setFormDuration(30);
    setFormAvailableDuration(30);
    setFormSlotType('Session');
    setFormInsertBeforeSlotId(undefined);
    setFormInsertAfterSlotId(undefined);
    setIsUntimedFormActive(false);
  }, []);

  const isFormActive = useMemo(
    () =>
      (formInsertBeforeSlotId !== undefined || formInsertAfterSlotId !== undefined || formOffset > 0) &&
      formDuration > 0,
    [formInsertAfterSlotId, formInsertBeforeSlotId, formDuration, formOffset],
  );

  const handleFormClose = useCallback(() => {
    resetFormState();
  }, [resetFormState]);

  useEffect(() => {
    if (editingTiming && isFormActive) {
      queueMicrotask(handleFormClose);
    }
  }, [editingTiming, isFormActive, handleFormClose]);

  useEffect(() => {
    if (isEditingEndTiming && isFormActive) {
      queueMicrotask(handleFormClose);
    }
  }, [isEditingEndTiming, isFormActive, handleFormClose]);

  const handleFormSuccess = useCallback(() => {
    resetFormState();
  }, [resetFormState]);

  const handleFormDurationChange = useCallback((duration: number) => {
    setFormDuration(duration);
  }, []);

  const handleShowForm = useCallback(
    (
      gapSlotIdOrPosition: string,
      offset: number,
      slotType: 'Session' | 'Break' = 'Session',
      insertBeforeSlotId?: string,
      insertAfterSlotId?: string,
    ) => {
      if (gapSlotIdOrPosition === 'untimed-top') {
        setFormInsertBeforeSlotId(undefined);
        setFormInsertAfterSlotId(undefined);
        setIsUntimedFormActive(true);
        setFormOffset(offset);
        setFormSlotType(slotType);
        setFormAvailableDuration(minuteBeforeMidnight);
        setFormDuration(slotType === 'Break' ? 30 : 30);
        return;
      }

      // Clear untimed form state when opening a timed form.
      setIsUntimedFormActive(false);
      setFormOffset(offset);
      setFormSlotType(slotType);
      if (gapSlotIdOrPosition === 'gap-after-end' && !insertBeforeSlotId && !insertAfterSlotId) {
        setFormInsertBeforeSlotId(undefined);
        setFormInsertAfterSlotId(END_GAP_ANCHOR_ID);
      } else {
        setFormInsertBeforeSlotId(insertBeforeSlotId);
        setFormInsertAfterSlotId(insertAfterSlotId);
      }

      const slotsSource = localSlots && localSlots.length > 0 ? localSlots : (flow?.slots ?? []);
      const slotsToCheck = slotsSource.filter(
        (slot) =>
          !(slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined)),
      );
      const flowToUse = previewFlow ?? flow;
      const hasFixedEndTime = flowToUse?.durationPlannedMinutes !== undefined && flowToUse.durationPlannedMinutes > 0;

      let availableDuration: number;
      const normalizedInsertAfterSlotId = insertAfterSlotId === END_GAP_ANCHOR_ID ? undefined : insertAfterSlotId;
      let startSearchIndex = 0;
      if (normalizedInsertAfterSlotId) {
        const afterIndex = slotsToCheck.findIndex((slot) => slot.id === normalizedInsertAfterSlotId);
        if (afterIndex !== -1) startSearchIndex = afterIndex + 1;
      } else if (insertBeforeSlotId) {
        const beforeIndex = slotsToCheck.findIndex((slot) => slot.id === insertBeforeSlotId);
        if (beforeIndex !== -1) startSearchIndex = beforeIndex;
      }

      const nextFixedSlot = slotsToCheck.find(
        (slot, index) => index >= startSearchIndex && slot.plannedOffsetMinutes !== undefined,
      );

      let availableDurationCappedByNextFixedStart = false;
      if (nextFixedSlot?.plannedOffsetMinutes !== undefined) {
        availableDuration = nextFixedSlot.plannedOffsetMinutes - offset;
        availableDurationCappedByNextFixedStart = true;
      } else if (hasFixedEndTime) {
        const endOffset = flowToUse?.durationPlannedMinutes ?? 0;
        availableDuration = endOffset - offset;
      } else {
        availableDuration = minuteBeforeMidnight - offset;
      }

      availableDuration = Math.max(0, availableDuration);

      const preferredDefaultMinutes = 30;
      const defaultDuration =
        availableDurationCappedByNextFixedStart && availableDuration > 0 && availableDuration < preferredDefaultMinutes
          ? availableDuration
          : preferredDefaultMinutes;
      setFormAvailableDuration(availableDuration);
      setFormDuration(defaultDuration);
    },
    [minuteBeforeMidnight, flow, previewFlow, localSlots],
  );

  return {
    formOffset,
    formDuration,
    formAvailableDuration,
    formSlotType,
    formInsertBeforeSlotId,
    formInsertAfterSlotId,
    isUntimedFormActive,
    isFormActive,
    setFormAvailableDuration,
    setFormSlotType,
    resetFormState,
    handleFormClose,
    handleFormSuccess,
    handleFormDurationChange,
    handleShowForm,
  };
}

export default useSlotListForm;
